import { SetupFooterProvider, useSetupFooterConfig } from "./context/SetupFooterContext";
import { useSetupNavigation } from "./hooks/useSetupNavigation";
import { SetupLayout } from "./components/SetupLayout";
import { useSetupData } from "./hooks/useSetupData";
import { useSetupSteps } from "./constants/steps";
import { Layout, Button, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import { FaCheck } from "react-icons/fa";
import { useRef, useState, useEffect } from "react";

function SetupFooterBar({ onBack }: { onBack?: () => void }) {
  const { t } = useTranslation();
  const config = useSetupFooterConfig();

  if (!config) return null;

  return (
    <div className="setup-floating-footer">
      {onBack && (
        <Button type="default" onClick={onBack} className="setup-floating-back-btn">
          {t("Back")}
        </Button>
      )}
      <Button
        type="primary"
        onClick={config.onSave}
        loading={config.loading}
        disabled={config.disabled}
        className="setup-floating-save-btn"
      >
        {t("Save & Next")}
      </Button>
    </div>
  );
}

function SetupContent() {
  const { current, next, prev, goTo } = useSetupNavigation();
  const { mainAddress, refetch, loading, companySettingLoading } = useSetupData(current);
  const steps = useSetupSteps(next, prev, mainAddress, refetch, loading, companySettingLoading);
  const { t } = useTranslation();

  const [animating, setAnimating] = useState(false);
  const [displayStep, setDisplayStep] = useState(current);
  const [animClass, setAnimClass] = useState("");
  const prevStepRef = useRef(current);

  useEffect(() => {
    if (current !== prevStepRef.current) {
      const dir = current > prevStepRef.current ? "forward" : "backward";
      setAnimClass(`step-exit-${dir}`);
      setAnimating(true);

      const timer = setTimeout(() => {
        setDisplayStep(current);
        setAnimClass(`step-enter-${dir}`);

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setAnimClass(`step-enter-${dir} step-enter-active`);
          });
        });

        const finishTimer = setTimeout(() => {
          setAnimClass("");
          setAnimating(false);
        }, 350);

        return () => clearTimeout(finishTimer);
      }, 250);

      prevStepRef.current = current;
      return () => clearTimeout(timer);
    }
  }, [current]);

  const progressPercent = Math.round((current / (steps.length - 1)) * 100);

  const handleStepClick = (index: number) => {
    if (index < current && !animating) {
      goTo(index);
    }
  };

  return (
    <Layout className="layout-container setup-layout">
      <div className="setup-stepper-header">
        <div className="setup-stepper-info">
          <span className="setup-stepper-progress">
            {t("Step {{current}} of {{total}}", { current: current + 1, total: steps.length })}
          </span>
          <h2 className="setup-stepper-title">{steps[current].title}</h2>
          <span className="setup-progress-percent">{progressPercent}%</span>
        </div>
        <div className="setup-progress-bar-container">
          <div className="setup-progress-bar-fill" style={{ width: `${progressPercent}%` }} />
        </div>
        <div className="setup-stepper-bar">
          {steps.map((step, index) => {
            const isCompleted = index < current;
            const isActive = index === current;

            return (
              <Tooltip
                key={step.title}
                title={
                  <span>
                    <strong>{step.title}</strong>
                    <br />
                    {step.description}
                  </span>
                }
                placement="bottom"
              >
                <div
                  className={`setup-stepper-step ${
                    isCompleted ? "completed" : isActive ? "active" : "pending"
                  } ${isCompleted ? "clickable" : ""}`}
                  onClick={() => handleStepClick(index)}
                >
                  <div
                    className={`setup-stepper-dot ${isCompleted && prevStepRef.current === index + 1 ? "just-completed" : ""}`}
                  >
                    {isCompleted ? (
                      <span className="setup-check-icon">
                        <FaCheck />
                      </span>
                    ) : (
                      <span>{index + 1}</span>
                    )}
                  </div>
                  <div className="setup-stepper-label">
                    <span className="setup-stepper-step-title">{step.title}</span>
                    <span className="setup-stepper-step-desc">{step.description}</span>
                  </div>
                  {index < steps.length - 1 && (
                    <div className="setup-stepper-connector">
                      <div
                        className="setup-stepper-connector-fill"
                        style={{ width: isCompleted ? "100%" : "0%" }}
                      />
                    </div>
                  )}
                </div>
              </Tooltip>
            );
          })}
        </div>
      </div>
      <SetupLayout>
        <div className={`setup-step-content ${animClass}`}>{steps[displayStep].content}</div>
      </SetupLayout>
      <SetupFooterBar onBack={current > 0 && current < steps.length - 1 ? prev : undefined} />
    </Layout>
  );
}

export default function Setup() {
  return (
    <SetupFooterProvider>
      <SetupContent />
    </SetupFooterProvider>
  );
}
